import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, formatCLP } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  BadgeCheck, ChevronLeft, Lock, Wallet, Upload, FileText,
  CheckCircle2, Download, X, Star, Handshake, Check,
} from "lucide-react";

const MODALITY = {
  frustrated: {
    label: "Mediación frustrada",
    desc: "Pago al confirmar el intento, independiente del acuerdo. El mediador sube el acta de frustración.",
    acta_label: "Acta de mediación frustrada",
  },
  agreement: {
    label: "Con acta de acuerdo",
    desc: "Pago contra la firma del acta de acuerdo entre las partes.",
    acta_label: "Acta de acuerdo",
  },
};

export default function MediationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [error, setError] = useState("");
  const [showPay, setShowPay] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/mediations/${id}`);
      setM(data.mediation);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (error) return <div className="container-app py-16 panel p-8 text-[hsl(var(--destructive))]" data-testid="md-error">{error}</div>;
  if (!m) return <div className="container-app py-16 text-muted-foreground">Cargando…</div>;

  const isRequester = user?.id === m.requester_id;
  const isMediator = user?.id === m.mediator_id;
  const mod = MODALITY[m.modality];
  const myRoleKey = isMediator ? "mediator" : "requester";
  const counterUsed = (m.counter_used || {})[myRoleKey] === true;
  const currentFee = m.current_fee_clp ?? m.fee_clp;
  const awaitingMe = (
    (m.status === "pending" && isMediator) ||
    (m.status === "countered" && m.awaiting_decision_from === myRoleKey)
  );

  const accept = async () => {
    if (!window.confirm(`¿Aceptar el honorario de ${formatCLP(currentFee)}? Una vez aceptada, el abogado deberá confirmar el Pago Protegido.`)) return;
    try { await api.post(`/mediations/${id}/accept`); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const submitReject = async ({ counter_fee_clp }) => {
    try {
      const body = counter_fee_clp ? { counter_fee_clp } : {};
      await api.post(`/mediations/${id}/reject`, body);
      setShowRejectModal(false);
      load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const cancel = async () => {
    if (!window.confirm("¿Cancelar esta solicitud?")) return;
    try { await api.post(`/mediations/${id}/cancel`); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const reassign = async () => {
    const newId = window.prompt("ID del nuevo mediador (cópialo desde el directorio):");
    if (!newId) return;
    try {
      const { data } = await api.post(`/mediations/${id}/reassign`, { new_mediator_id: newId });
      nav(`/app/mediaciones/${data.id}`);
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const stepIndex = ({
    pending: 0, accepted: 1, in_progress: 2, awaiting_approval: 3, completed: 4,
    cancelled: -1, rejected: -1,
  })[m.status] ?? 0;

  return (
    <div className="container-app py-12" data-testid="mediation-detail">
      <button onClick={()=>nav(-1)} className="text-sm inline-flex items-center text-muted-foreground hover:text-foreground"><ChevronLeft size={14}/>Volver</button>

      <ProgressSteps current={stepIndex} cancelled={m.status === "cancelled" || m.status === "rejected"}/>

      <div className="mt-8 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="pill"><Handshake size={12}/>{mod.label}</span>
            <span className="pill badge-cyan"><Lock size={12}/>Operación anónima</span>
            {m.mediator_verified && <span className="pill badge-verified"><BadgeCheck size={12}/>Mediador verificado</span>}
          </div>
          <h1 className="serif text-5xl mt-4 leading-tight">{m.subject}</h1>
          <p className="mt-3 text-muted-foreground">{mod.desc}</p>

          <dl className="mt-8 grid sm:grid-cols-2 gap-px bg-[hsl(var(--border))] border rounded-lg overflow-hidden" style={{ borderColor: "hsl(var(--border))" }}>
            <Item label="Solicitante" value={isRequester ? `${m.requester_name} (tú)` : "Identidad reservada"}/>
            <Item label="Mediador" value={isMediator ? `${m.mediator_name} (tú)` : m.mediator_name}/>
            <Item label="Modalidad" value={mod.label}/>
            <Item label="Honorario actual" value={formatCLP(currentFee)}/>
            <Item label="Partes" value={m.parties_description || "—"}/>
            <Item label="Ubicación" value={`${m.mediator_comuna || "—"}, ${m.mediator_region || "—"}`}/>
          </dl>

          {m.notes && (
            <div className="mt-8 panel p-6">
              <div className="label-eyebrow">Notas</div>
              <p className="mt-3 text-foreground/90 leading-relaxed">{m.notes}</p>
            </div>
          )}

          {/* Files section (visible to both during/after escrow) */}
          {["in_progress", "awaiting_approval", "completed"].includes(m.status) && (
            <FilesSection mediation={m} reload={load} isRequester={isRequester} isMediator={isMediator} mod={mod}/>
          )}
        </div>

        <aside className="space-y-6">
          {/* Awaiting decision (any role): show accept / reject */}
          {awaitingMe && (
            <div className="panel-glow p-6" data-testid="awaiting-decision-panel">
              <div className="label-eyebrow text-[hsl(var(--primary))]">
                {m.status === "pending" ? "Paso 1 · Respuesta" : "Contrapropuesta recibida"}
              </div>
              <h3 className="serif text-2xl mt-2">
                {m.status === "pending" ? "Solicitud recibida" : "Nueva oferta de honorarios"}
              </h3>
              <p className="text-sm text-muted-foreground mt-2">
                {m.status === "pending"
                  ? <>{m.requester_name} te propone <strong>{formatCLP(currentFee)}</strong> bajo modalidad <strong>{mod.label.toLowerCase()}</strong>.</>
                  : <>La contraparte propone ahora <strong>{formatCLP(currentFee)}</strong>. Tu intento de contrapropuesta {counterUsed ? "ya fue usado" : "sigue disponible"}.</>
                }
              </p>
              {(m.fee_history || []).length > 1 && (
                <ol className="mt-3 text-xs text-muted-foreground space-y-1">
                  {(m.fee_history || []).map((h, i) => (
                    <li key={`${h.at}-${h.by_role}`}>
                      {h.by_role === "requester" ? "Abogado" : "Mediador"}: <strong>{formatCLP(h.amount)}</strong>
                    </li>
                  ))}
                </ol>
              )}
              <div className="mt-5 flex gap-2 flex-wrap">
                <button onClick={accept} className="btn-primary flex-1" data-testid="accept-mediation">
                  <Check size={14} className="mr-1.5"/>Aceptar {formatCLP(currentFee)}
                </button>
                <button onClick={()=>setShowRejectModal(true)} className="btn-ghost flex-1" data-testid="reject-mediation">
                  <X size={14} className="mr-1.5"/>Rechazar
                </button>
              </div>
            </div>
          )}

          {/* Waiting for the other side (after I countered) */}
          {!awaitingMe && m.status === "countered" && (
            <div className="panel p-6">
              <div className="label-eyebrow">Esperando respuesta</div>
              <h3 className="serif text-xl mt-2">Contraparte revisando tu propuesta</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Tu propuesta vigente: <strong>{formatCLP(currentFee)}</strong>. Te avisaremos cuando responda.
              </p>
            </div>
          )}

          {/* Rejected → option to reassign (requester only) */}
          {m.status === "rejected" && isRequester && (
            <div className="panel-glow p-6">
              <div className="label-eyebrow text-[hsl(var(--destructive))]">Mediación rechazada</div>
              <h3 className="serif text-2xl mt-2">No hubo acuerdo</h3>
              <p className="text-sm text-muted-foreground mt-2">
                {m.rejected_by_role === "mediator" ? "El mediador" : "Tú"} rechazaste definitivamente.
                Puedes reenviar la solicitud a otro mediador con los mismos datos.
              </p>
              <button onClick={reassign} className="btn-primary w-full mt-4" data-testid="reassign-mediation">
                Reasignar a otro mediador
              </button>
            </div>
          )}

          {/* REQUESTER: Accepted → pay */}
          {isRequester && m.status === "accepted" && m.payment_status === "pending_payment" && (
            <div className="panel-glow p-6">
              <div className="label-eyebrow text-[hsl(var(--primary))]">Paso 2 · Pago Protegido Colega Link</div>
              <h3 className="serif text-2xl mt-2">Activar mediación</h3>
              <p className="text-sm text-muted-foreground mt-2">
                {m.mediator_name} aceptó. Confirma el Pago Protegido para que pueda agendar y subir antecedentes.
              </p>
              <button onClick={()=>setShowPay(true)} className="btn-primary w-full mt-5" data-testid="pay-mediation"><Wallet size={16} className="mr-2"/>Confirmar Pago Protegido</button>
              <p className="text-xs text-muted-foreground mt-3 text-center">Simulado · Sujeto a integración de medios de pago</p>
            </div>
          )}

          {/* MEDIATOR: In progress → upload acta */}
          {isMediator && m.status === "in_progress" && (
            <div className="panel-glow p-6">
              <div className="label-eyebrow text-[hsl(var(--primary))]">Paso 3 · Acta</div>
              <h3 className="serif text-2xl mt-2">Cuando termines, sube el acta</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Sube el documento: <strong>{mod.acta_label}</strong>. Al subirlo, el solicitante puede aprobar y se libera tu pago.
              </p>
              <FileUploader mediationId={id} kind="acta" onUploaded={load}/>
            </div>
          )}

          {/* REQUESTER: Awaiting approval → release */}
          {isRequester && m.status === "awaiting_approval" && (
            <div className="panel-glow p-6">
              <div className="label-eyebrow text-[hsl(var(--accent))]">Paso 4 · Liberar pago</div>
              <h3 className="serif text-2xl mt-2">Acta recibida</h3>
              <p className="text-sm text-muted-foreground mt-2">Revisa el acta y, si está conforme, libera el pago.</p>
              <button onClick={()=>setShowApprove(true)} className="btn-primary w-full mt-5" data-testid="approve-mediation"><CheckCircle2 size={16} className="mr-2"/>Aprobar y liberar pago</button>
            </div>
          )}

          <div className="panel p-6">
            <div className="label-eyebrow">Estado del pago</div>
            <div className="mt-2 text-sm">
              {m.payment_status === "none" && <span className="pill">Sin pago</span>}
              {m.payment_status === "pending_payment" && <span className="pill badge-pending">Pendiente</span>}
              {m.payment_status === "in_escrow" && <span className="pill badge-cyan">Pago Protegido activo</span>}
              {m.payment_status === "released" && <span className="pill badge-verified">Liberado</span>}
            </div>
            <div className="mt-3 text-xs text-muted-foreground">Honorario: {formatCLP(m.fee_clp)}</div>
          </div>

          {isRequester && (m.status === "pending" || m.status === "accepted") && (
            <button onClick={cancel} className="btn-danger w-full" data-testid="cancel-mediation"><X size={14} className="mr-1.5"/>Cancelar solicitud</button>
          )}
        </aside>
      </div>

      {showPay && <PayModal mediationId={id} onClose={()=>setShowPay(false)} onPaid={()=>{setShowPay(false); load();}}/>}
      {showApprove && <ApproveModal mediationId={id} onClose={()=>setShowApprove(false)} onApproved={()=>{setShowApprove(false); load();}}/>}
      {showRejectModal && (
        <RejectChoiceModal
          currentFee={currentFee}
          counterUsed={counterUsed}
          onClose={()=>setShowRejectModal(false)}
          onSubmit={submitReject}
          contextLabel="mediación"
        />
      )}
    </div>
  );
}

function RejectChoiceModal({ currentFee, counterUsed, onClose, onSubmit, contextLabel }) {
  const [mode, setMode] = useState("choose"); // choose | counter | confirm_final
  const [fee, setFee] = useState(currentFee);
  const [busy, setBusy] = useState(false);
  const submitCounter = async () => {
    const n = Number(fee);
    if (!n || n <= 0) { alert("Ingresa un monto válido."); return; }
    setBusy(true);
    try { await onSubmit({ counter_fee_clp: n }); } finally { setBusy(false); }
  };
  const submitFinal = async () => {
    setBusy(true);
    try { await onSubmit({}); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="reject-choice-modal">
      <div className="label-eyebrow text-[hsl(var(--destructive))]">Rechazar {contextLabel}</div>
      <h2 className="serif text-3xl mt-2">¿Qué deseas hacer?</h2>
      {mode === "choose" && (
        <div className="mt-6 space-y-3">
          <button
            onClick={()=>setMode("counter")}
            disabled={counterUsed}
            className={`w-full text-left panel p-4 hover:border-[hsl(var(--primary))]/60 transition ${counterUsed ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="reject-option-counter"
          >
            <div className="serif text-lg">Proponer nuevo monto de honorario</div>
            <div className="text-xs text-muted-foreground mt-1">
              {counterUsed
                ? "Ya usaste tu intento de contrapropuesta para esta operación."
                : "Tienes 1 intento. Tu propuesta llegará a la contraparte para aceptar o rechazar."}
            </div>
          </button>
          <button
            onClick={()=>setMode("confirm_final")}
            className="w-full text-left panel p-4 hover:border-[hsl(var(--destructive))]/60 transition"
            data-testid="reject-option-final"
          >
            <div className="serif text-lg">Rechazar definitivamente</div>
            <div className="text-xs text-muted-foreground mt-1">
              La oferta se libera y el abogado podrá reasignarla a otro profesional.
            </div>
          </button>
        </div>
      )}
      {mode === "counter" && (
        <div className="mt-6">
          <div className="label-eyebrow">Nuevo honorario propuesto (CLP)</div>
          <input
            className="field mt-2"
            type="number"
            min={1000}
            step={1000}
            value={fee}
            onChange={(e)=>setFee(e.target.value)}
            data-testid="reject-counter-input"
          />
          <div className="mt-4 flex gap-3">
            <button onClick={submitCounter} disabled={busy} className="btn-primary flex-1" data-testid="reject-counter-submit">
              {busy ? "Enviando…" : "Enviar contrapropuesta"}
            </button>
            <button onClick={()=>setMode("choose")} className="btn-ghost">Atrás</button>
          </div>
        </div>
      )}
      {mode === "confirm_final" && (
        <div className="mt-6">
          <p className="text-sm">Esta acción no se puede deshacer. La oferta se liberará inmediatamente.</p>
          <div className="mt-4 flex gap-3">
            <button onClick={submitFinal} disabled={busy} className="btn-danger flex-1" data-testid="reject-final-submit">
              {busy ? "Procesando…" : "Sí, rechazar definitivamente"}
            </button>
            <button onClick={()=>setMode("choose")} className="btn-ghost">Atrás</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Item({ label, value }) {
  return (
    <div className="bg-[hsl(var(--card))] p-5">
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1.5 text-base">{value}</div>
    </div>
  );
}

function ProgressSteps({ current, cancelled }) {
  const steps = ["Pendiente", "Aceptada · pago", "En proceso", "Acta", "Completada"];
  if (cancelled) return <div className="mt-8 pill badge-pending">Esta mediación fue cancelada o rechazada</div>;
  return (
    <ol className="mt-8 flex items-center gap-3 sm:gap-5 overflow-x-auto" data-testid="md-progress">
      {steps.map((s, i) => (
        <li key={s} className="flex items-center gap-3 shrink-0">
          <span className={`step-dot ${i < current ? "done" : i === current ? "current" : ""}`}>{i + 1}</span>
          <span className={`text-xs ${i <= current ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
          {i < steps.length - 1 && <span className="w-6 h-px bg-[hsl(var(--border))]"/>}
        </li>
      ))}
    </ol>
  );
}

function FilesSection({ mediation: m, reload, isRequester, isMediator, mod }) {
  return (
    <section className="mt-12">
      <h2 className="serif text-3xl">Documentos</h2>
      <p className="text-sm text-muted-foreground mt-1">Solo visibles para el solicitante y el mediador.</p>
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div className="panel p-5">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Antecedentes del caso</div>
            <span className="pill">{(m.case_files || []).length} arch.</span>
          </div>
          <FileList files={m.case_files || []} mediationId={m.id}/>
          {isRequester && m.payment_status === "in_escrow" && (
            <FileUploader mediationId={m.id} kind="case" onUploaded={reload}/>
          )}
        </div>
        <div className="panel p-5">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">{mod.acta_label}</div>
            <span className="pill badge-verified">{(m.acta_files || []).length} arch.</span>
          </div>
          <FileList files={m.acta_files || []} mediationId={m.id}/>
        </div>
      </div>
    </section>
  );
}

function FileList({ files, mediationId }) {
  if (!files || files.length === 0) return <p className="mt-4 text-sm text-muted-foreground">Sin archivos.</p>;
  return (
    <ul className="mt-4 space-y-2" data-testid={`md-files-${mediationId}`}>
      {files.map((f) => (
        <li key={f.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`md-file-${f.id}`}>
          <span className="inline-flex items-center gap-2 truncate"><FileText size={14} className="text-[hsl(var(--primary))] shrink-0"/>{f.filename}</span>
          <button onClick={()=>downloadFile(mediationId, f)} className="text-xs link-underline inline-flex items-center gap-1" data-testid={`md-download-${f.id}`}><Download size={12}/>Descargar</button>
        </li>
      ))}
    </ul>
  );
}

async function downloadFile(mediationId, file) {
  try {
    const { data } = await api.get(`/mediations/${mediationId}/files/${file.id}`);
    const raw = data.file.data_b64;
    const bytes = Uint8Array.from(atob(raw), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: file.mime || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = file.filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
}

function FileUploader({ mediationId, kind, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const handle = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr("Archivo demasiado grande (máx 5MB)"); return; }
    setBusy(true); setErr("");
    try {
      const data_b64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(",")[1]);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      await api.post(`/mediations/${mediationId}/files`, {
        filename: file.name, mime: file.type || "application/octet-stream", data_b64, kind,
      });
      onUploaded();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handle(f);
  };

  return (
    <div className="mt-4">
      <label
        className={`drop-zone block transition ${dragOver ? "ring-2 ring-[hsl(var(--primary))]/60 bg-[hsl(var(--primary))]/5" : ""}`}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid={`md-uploader-${kind}`}
      >
        <Upload size={20} className="mx-auto text-[hsl(var(--primary))]"/>
        <div className="serif text-lg mt-3">{busy ? "Subiendo…" : dragOver ? "Suelta para subir" : "Arrastra o haz clic para subir"}</div>
        <p className="text-xs text-muted-foreground mt-1">PDF, JPG o PNG · máx 5MB</p>
        <input ref={inputRef} type="file" accept=".pdf,image/*" className="hidden"
          onChange={(e) => handle(e.target.files?.[0])} disabled={busy}/>
      </label>
      {err && <div className="mt-2 text-sm text-[hsl(var(--destructive))]">{err}</div>}
    </div>
  );
}

function PayModal({ mediationId, onClose, onPaid }) {
  const [quote, setQuote] = useState(null);
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/mediations/${mediationId}/quote`); setQuote(data); }
      catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    })();
  }, [mediationId]);
  const pay = async () => {
    setPaying(true); setErr("");
    try { await api.post(`/mediations/${mediationId}/pay`); onPaid(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); setPaying(false); }
  };
  return (
    <Modal onClose={onClose} testid="md-pay-modal">
      <div className="label-eyebrow text-[hsl(var(--primary))]">Pago Protegido Colega Link</div>
      <h2 className="serif text-3xl mt-2">Confirmar Pago Protegido</h2>
      {!quote ? <div className="mt-6 text-muted-foreground">Cargando…</div> : (
        <>
          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex justify-between"><span>Honorario del mediador</span><span className="font-medium">{formatCLP(quote.fee_clp)}</span></li>
            <li className="flex justify-between"><span>Cargo Colega Link</span><span className="font-medium">{formatCLP(quote.commission_clp)}</span></li>
            <li className="text-xs text-muted-foreground -mt-1">{quote.commission_label}</li>
            {(quote.commission_min_applied || quote.commission_reference_applied) && (
              <li className="text-xs text-amber-200/90 p-2 rounded-md bg-amber-400/10 border border-amber-400/25" data-testid="pay-med-min-notice">
                {quote.commission_min_applied && !quote.commission_reference_applied && (
                  <>Se aplicó el <strong>cargo mínimo Colega Link ({formatCLP(quote.commission_min_clp)})</strong> porque el {quote.commission_pct}% del honorario resultaba menor.</>
                )}
                {quote.commission_reference_applied && !quote.commission_min_applied && (
                  <>El cargo se calcula sobre la <strong>tarifa referencial de mercado ({formatCLP(quote.commission_reference_clp)})</strong>, no sobre el honorario declarado.</>
                )}
                {quote.commission_min_applied && quote.commission_reference_applied && (
                  <>Se aplicó el <strong>cargo mínimo Colega Link ({formatCLP(quote.commission_min_clp)})</strong> — la tarifa referencial ({formatCLP(quote.commission_reference_clp)}) generaba un cargo menor.</>
                )}
              </li>
            )}
            <li className="flex justify-between pt-3 border-t" style={{ borderColor: "hsl(var(--border))" }}>
              <span className="serif text-xl">Total</span>
              <span className="serif text-xl brand-gradient-text">{formatCLP(quote.total_clp)}</span>
            </li>
          </ul>
          {err && <div className="mt-4 text-sm text-[hsl(var(--destructive))]">{err}</div>}
          <div className="mt-6 flex gap-3">
            <button onClick={pay} disabled={paying} className="btn-primary flex-1" data-testid="md-confirm-pay">{paying ? "Procesando…" : `Pagar ${formatCLP(quote.total_clp)}`}</button>
            <button onClick={onClose} className="btn-ghost">Cancelar</button>
          </div>
        </>
      )}
    </Modal>
  );
}

function ApproveModal({ mediationId, onClose, onApproved }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const canSubmit = rating >= 1 && rating <= 5 && comment.trim().length >= 5;
  const approve = async () => {
    if (!canSubmit) { setErr("Debes seleccionar 1 a 5 estrellas y escribir al menos 5 caracteres."); return; }
    setSaving(true); setErr("");
    try { await api.post(`/mediations/${mediationId}/approve`, { rating, comment: comment.trim() }); onApproved(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); setSaving(false); }
  };
  return (
    <Modal onClose={onClose} testid="md-approve-modal">
      <div className="label-eyebrow text-[hsl(var(--accent))]">Liberación de pago</div>
      <h2 className="serif text-3xl mt-2">Aprobar mediación</h2>
      <p className="mt-3 text-sm text-muted-foreground">Califica al mediador. La reseña queda pública en su perfil.</p>
      <p className="mt-3 p-3 rounded-md border border-cyan-400/25 bg-cyan-400/[0.05] text-xs text-cyan-100">
        Tu calificación ayuda a que otros colegas confíen y mantiene la calidad de ColegaLink.
      </p>
      <div className="mt-5">
        <div className="label-eyebrow mb-2">Calificación · obligatoria *</div>
        <div className="flex items-center gap-2">
          {[1,2,3,4,5].map((n) => (
            <button type="button" key={n} onClick={()=>setRating(n)}
              className={`w-10 h-10 rounded-md inline-flex items-center justify-center border ${n <= rating ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent" : "border-[hsl(var(--border))] text-muted-foreground"}`}><Star size={14}/></button>
          ))}
        </div>
      </div>
      <div className="mt-4">
        <label className="label-eyebrow">Comentario · obligatorio *</label>
        <textarea
          className="field mt-2" rows={3} required minLength={5}
          placeholder="Cuenta cómo fue la experiencia (mínimo 5 caracteres)."
          value={comment} onChange={(e) => setComment(e.target.value)}
        />
        <div className="mt-1 text-[10px] text-muted-foreground text-right">{comment.trim().length}/5+ caracteres</div>
      </div>
      {err && <div className="mt-4 text-sm text-[hsl(var(--destructive))]">{err}</div>}
      <div className="mt-6 flex gap-3">
        <button onClick={approve} disabled={saving || !canSubmit} className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"><CheckCircle2 size={16} className="mr-2"/>{saving ? "Liberando…" : "Aprobar y liberar pago"}</button>
        <button onClick={onClose} className="btn-ghost" disabled={saving}>Cancelar</button>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur overflow-y-auto" data-testid={testid}>
      <div className="panel p-8 max-w-md w-full relative max-h-[90vh] overflow-y-auto my-4">
        <button onClick={onClose} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"><X size={18}/></button>
        {children}
      </div>
    </div>
  );
}
